'use client';

import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';

import type { DestinationTile } from '@/app/api/lib/destinations/destination.types';
import type { ZoneGroup } from '@/components/homepage/groupByZone';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

type Props = {
    groups: ZoneGroup[];
    scrollableTabs?: boolean;
    /** Fond sombre — tabs et indicateur en blanc. */
    dark?: boolean;
    children: (tiles: DestinationTile[]) => React.ReactNode;
};

export function DestinationsZoneTabs({
    groups,
    scrollableTabs = false,
    dark = false,
    children,
}: Props) {
    const theme = useTheme();
    const [activeIndex, setActiveIndex] = useState(0);
    const activeTiles = groups[activeIndex]?.tiles ?? [];

    const inactiveColor = dark ? alpha(theme.palette.common.white, 0.55) : 'text.secondary';
    const activeColor = dark ? 'common.white' : 'text.primary';
    const indicatorColor = dark ? 'common.white' : 'text.primary';
    const borderColor = dark ? alpha(theme.palette.common.white, 0.15) : 'divider';

    return (
        <Box>
            <Tabs
                value={activeIndex}
                onChange={(_, idx: number) => {
                    trackNavigationClick({
                        navItem: 'zone_tab',
                        triggerContext: 'destinations_mega_menu',
                    });
                    setActiveIndex(idx);
                }}
                variant={scrollableTabs ? 'scrollable' : 'standard'}
                scrollButtons={scrollableTabs ? 'auto' : false}
                sx={{
                    borderBottom: 1,
                    borderColor,
                    minHeight: 40,
                    '& .MuiTab-root': {
                        minHeight: 40,
                        py: 0.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        color: inactiveColor,
                    },
                    '& .MuiTab-root.Mui-selected': {
                        color: activeColor,
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: indicatorColor,
                    },
                }}
            >
                {groups.map((g) => (
                    <Tab key={g.slug} label={g.label} />
                ))}
            </Tabs>
            <Box sx={{ pt: 1.5 }}>{children(activeTiles)}</Box>
        </Box>
    );
}
